import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import bookingService from '../../../../../../services/booking.service';
import { useEyeconxStore } from '../../../../../../store/eyeconxStore';
import { showError, showSuccess } from '../../../../../../utils/ecxSwal';
import { ClinicInfoPanel, ClinicInfoDetailItem, ClinicInfoFormField } from '../../info/clinicInfoParts';
import ClinicSettingsCancelButton from '../../subparts/ClinicSettingsCancelButton';
import ClinicSettingsFormFooter from '../../subparts/ClinicSettingsFormFooter';

const getInitials = (name) => {
    if (!name) return 'DR';
    return name
        .split(' ')
        .map((part) => part[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
};

const DoctorsCard = ({ doctor }) => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditingBio, setIsEditingBio] = useState(false);
    const [doctorInfo, setDoctorInfo] = useState(null);

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: { bio: '' },
    });

    const fetchDoctorInfo = useCallback(async () => {
        if (!doctor?.id) return;

        setIsLoading(true);
        try {
            const res = await bookingService.getDoctorDetails(clinicId, doctor.id);
            setDoctorInfo(res.data);
            reset({ bio: res.data?.bio || '' });
        } catch (error) {
            console.log(error);
            setDoctorInfo(doctor);
            reset({ bio: doctor?.bio || '' });
        } finally {
            setIsLoading(false);
        }
    }, [clinicId, doctor, reset]);

    useEffect(() => {
        fetchDoctorInfo();
    }, [fetchDoctorInfo]);

    const handleEditBio = () => {
        reset({ bio: doctorInfo?.bio || doctor?.bio || '' });
        setIsEditingBio(true);
    };

    const handleCancelBio = () => {
        reset({ bio: doctorInfo?.bio || '' });
        setIsEditingBio(false);
    };

    const handleSaveBio = async ({ bio }) => {
        const profile = doctorInfo || doctor;
        setIsSaving(true);

        try {
            const payload = {
                id: profile.id,
                name: profile.name,
                bio: bio?.trim() || '',
                isActive: profile.isActive ?? doctor?.isActive ?? false,
                photoUrl: profile.photoUrl || '',
            };

            const res = await bookingService.updateDoctorDetails(clinicId, profile.id, payload);
            setDoctorInfo(res.data);
            reset({ bio: res.data?.bio || '' });
            setIsEditingBio(false);

            await showSuccess({
                title: 'Bio Updated',
                text: 'The doctor bio was saved successfully.',
            });
        } catch (error) {
            console.log(error);
            showError({
                title: 'Save Failed',
                text: 'Unable to update the doctor bio. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleActiveToggle = async () => {
        const profile = doctorInfo || doctor;
        if (!profile?.id) {
            return;
        }

        const nextActive = !(profile.isActive ?? doctor?.isActive);
        setIsSaving(true);

        try {
            const payload = {
                id: profile.id,
                name: profile.name,
                bio: profile.bio || '',
                isActive: nextActive,
                photoUrl: profile.photoUrl || '',
            };

            const res = await bookingService.updateDoctorDetails(clinicId, profile.id, payload);
            setDoctorInfo(res.data);

            await showSuccess({
                title: nextActive ? 'Doctor Activated' : 'Doctor Deactivated',
                text: nextActive
                    ? 'This doctor is now visible on the web booking page.'
                    : 'This doctor is now hidden from the web booking page.',
            });
        } catch (error) {
            console.log(error);
            showError({
                title: 'Update Failed',
                text: 'Unable to update doctor availability status.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const profile = doctorInfo || doctor;
    const displayName = profile?.name || 'Unknown Doctor';
    const isActive = profile?.isActive ?? doctor?.isActive;

    const panelActions = !isEditingBio ? (
        <button
            type="button"
            className="btn btn-sm btn-light-primary rounded-pill"
            onClick={handleEditBio}
        >
            <i className="bi bi-pencil me-1"></i>
            Edit Bio
        </button>
    ) : (
        <ClinicSettingsCancelButton onClick={handleCancelBio} disabled={isSaving} />
    );

    if (isLoading) {
        return (
            <ClinicInfoPanel title="Doctor Profile" icon="bi-person-badge">
                <div className="doctor-profile-skeleton">
                    <div className="doctor-profile-skeleton__avatar" />
                    <div className="doctor-profile-skeleton__content">
                        <div className="clinic-settings-skeleton__line clinic-settings-skeleton__line--medium mb-2" />
                        <div className="clinic-settings-skeleton__line clinic-settings-skeleton__line--short mb-3" />
                        <div className="clinic-settings-skeleton__line clinic-settings-skeleton__line--long" />
                    </div>
                </div>
            </ClinicInfoPanel>
        );
    }

    return (
        <ClinicInfoPanel title="Doctor Profile" icon="bi-person-badge" actions={panelActions}>
            <div className="doctor-profile">
                <div className="doctor-profile__aside">
                    <div className="doctor-profile__avatar">
                        {profile?.photoUrl ? (
                            <img src={profile.photoUrl} alt={displayName} />
                        ) : (
                            <span>{getInitials(displayName)}</span>
                        )}
                    </div>
                    <h5 className="doctor-profile__name">{displayName}</h5>
                    <div className="d-flex flex-column align-items-center gap-2">
                        <span className={`clinic-settings-status-pill ${isActive ? 'is-open' : 'is-closed'}`}>
                            <i className={`bi ${isActive ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                            {isActive ? 'Active' : 'Inactive'}
                        </span>
                        <div className="form-check form-switch mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id={`doctor-active-${profile?.id}`}
                                checked={Boolean(isActive)}
                                onChange={handleActiveToggle}
                                disabled={isSaving || isEditingBio}
                            />
                            <label className="form-check-label small" htmlFor={`doctor-active-${profile?.id}`}>
                                Show on web booking
                            </label>
                        </div>
                    </div>
                </div>
                <div className="doctor-profile__details">
                    {isEditingBio ? (
                        <form onSubmit={handleSubmit(handleSaveBio)} className="doctor-profile__bio-form">
                            <ClinicInfoFormField
                                label="Bio"
                                htmlFor="doctor-bio"
                                error={errors.bio?.message}
                                hint="This bio is shown to patients on the web booking page."
                                className="clinic-info-detail-item--full"
                            >
                                <textarea
                                    id="doctor-bio"
                                    className={`form-control ${errors.bio ? 'is-invalid' : ''}`}
                                    rows={5}
                                    placeholder="Write a short professional bio for this doctor..."
                                    {...register('bio', {
                                        maxLength: {
                                            value: 2000,
                                            message: 'Bio must be 2000 characters or less',
                                        },
                                    })}
                                />
                            </ClinicInfoFormField>
                            <ClinicSettingsFormFooter isSaving={isSaving} submitLabel="Save Bio" />
                        </form>
                    ) : (
                        <div className="clinic-info-detail-grid">
                            <ClinicInfoDetailItem label="Bio" className="clinic-info-detail-item--full">
                                {profile?.bio?.trim() ? profile.bio : 'No bio provided.'}
                            </ClinicInfoDetailItem>
                            {profile?.email && (
                                <ClinicInfoDetailItem label="Email" value={profile.email} />
                            )}
                            {profile?.phone && (
                                <ClinicInfoDetailItem label="Phone" value={profile.phone} />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </ClinicInfoPanel>
    );
};

export default DoctorsCard;
